/**
 * THE TOKENIZATION LIFECYCLE, AS A STATEFUL FIXTURE.
 *
 * `mocks.ts` is a stage set: a fixed object per endpoint, so a screen renders with the backend off.
 * That is right for a wallet balance and wrong for this, because the whole subject here is a THING
 * THAT MOVES. A fixture that returned a constant application would let us build a progress tracker
 * that has never advanced, an offer screen whose Approve button has never changed anything, and a
 * listing form that has never reduced an available balance. Every one of those is a screen that looks
 * finished and has never been exercised.
 *
 * So this module keeps state for the session and applies the SAME state machine the database does.
 * The transition table below is copied from `tokenization.lifecycle_transition` (migration 0055) and
 * refuses the same moves, so a screen that works here is a screen that works against the service.
 *
 * ## What it deliberately does NOT pretend
 *
 * The four parties live on four origins and this app is one of them. An owner cannot review their own
 * submission, and there is no route here that lets them. What the fixture does instead is ADVANCE the
 * other parties' steps on a timer once the owner has done their part, so the walk is demonstrable in
 * one browser - and `advancedBy` records which party the fixture acted as, so nothing on screen reads
 * as the owner having done it.
 *
 * Nothing here is a control. Flip `VITE_UI_MOCKS` off and every one of these calls goes to
 * svc-tokenization, where the refusals are triggers rather than `if` statements.
 */

import { docLabel } from './tokenization';
import type {
  ApplicationSummary,
  ChecklistItem,
  LifecycleEvent,
  ProductConfig,
  SecondaryListing,
  TokenHolding,
  TokenizationApplication,
  TokenizationOffer,
  TokenizationProduct,
  TokenizationStatus,
  UploadedDocument,
  UploadPolicy,
} from './api';

const iso = (offsetMs = 0) => new Date(Date.now() + offsetMs).toISOString();
const DAY = 86_400_000;
const id = (prefix: string) => `${prefix}-${Math.floor(Math.random() * 1e9).toString(36)}`;

/** One whole token in minor units, matching lipa.contracts.money.TOKEN_SCALE. */
const ONE_TOKEN = 100_000_000n;

const DEMO_OWNER = 'demo0001-0000-4000-8000-000000000001';

/**
 * The stage map, copied from `LIFECYCLE_STAGES` in lipa/contracts/domain.py.
 *
 * Copied rather than derived, and that is a known duplication with a known cost: if the backend adds a
 * status and this is not updated, the tracker here shows the wrong stage. The alternative was to fetch
 * the stages, which is what the app ACTUALLY does - `application.stages` comes off the wire. This
 * table exists only so the fixture can produce that field.
 */
const STAGES: [string, TokenizationStatus[]][] = [
  ['Asset submitted', ['draft', 'submitted']],
  ['Platform review', ['under_vasp_review', 'information_requested']],
  ['Trust review', ['under_trust_review']],
  ['Valuation', ['approved_for_valuation', 'valuation_in_progress', 'valuation_submitted']],
  ['Valuation reconciliation', ['under_trust_reconciliation']],
  ['Tokenization offer', ['tokenization_offer_pending', 'offer_pending_user_approval']],
  ['Approval', ['offer_pending_vasp_approval', 'tokenization_authorized']],
  ['Token issuance', ['tokens_issued']],
  ['Secondary market', ['listed_on_secondary_market', 'completed']],
];

const TERMINAL: TokenizationStatus[] = ['rejected', 'withdrawn'];

function stagesFor(status: TokenizationStatus) {
  if (TERMINAL.includes(status))
    return STAGES.map(([label]) => ({ label, state: 'pending' as const }));
  const current = Math.max(
    0,
    STAGES.findIndex(([, members]) => members.includes(status)),
  );
  return STAGES.map(([label], i) => ({
    label,
    state: (i < current ? 'done' : i === current ? 'current' : 'pending') as
      'done' | 'current' | 'pending',
  }));
}

// ---------------------------------------------------------------------------
// THE PRODUCTS. The same five the migration seeds, with the property one in full.
// ---------------------------------------------------------------------------

const PRODUCTS: TokenizationProduct[] = [
  {
    id: 'prod-property',
    code: 'property_fractional',
    asset_class: 'real_estate',
    name: 'Fractional property',
    summary:
      'A building or a unit, held by a special purpose vehicle, with fractional interests issued against it.',
    structure: 'Fractional beneficial interest in the SPV that holds the title',
    classification: 'asset_backed',
  },
  {
    id: 'prod-land',
    code: 'land_fractional',
    asset_class: 'land',
    name: 'Fractional land',
    summary:
      'A titled parcel, held by a special purpose vehicle, with fractional interests issued against it.',
    structure: 'Fractional beneficial interest in the SPV that holds the title',
    classification: 'asset_backed',
  },
  {
    id: 'prod-vehicle',
    code: 'vehicle_fleet',
    asset_class: 'vehicle',
    name: 'Vehicle or fleet',
    summary: 'One vehicle or a fleet, tokenized against its logbook value and its income.',
    structure: 'Fractional beneficial interest in the vehicle or fleet',
    classification: 'asset_backed',
  },
  {
    id: 'prod-receivable',
    code: 'receivable_discount',
    asset_class: 'receivable',
    name: 'Receivable',
    summary: 'An invoice or a contracted future cash flow, tokenized at a discount to face value.',
    structure: 'Fractional interest in the receivable and its proceeds',
    classification: 'security',
  },
  {
    id: 'prod-inventory',
    code: 'inventory_commodity',
    asset_class: 'inventory',
    name: 'Inventory or commodity',
    summary: 'Stored, verifiable inventory: coffee, tea, metals, produce, equipment held for sale.',
    structure: 'Fractional interest in the stored inventory',
    classification: 'asset_backed',
  },
];

type Field = ProductConfig['fields'][number];

const COMMON_FIELDS: Field[] = [
  {
    section: 'asset',
    key: 'description',
    label: 'Description',
    kind: 'long_text',
    unit: null,
    required: true,
    help: 'What the asset is, in the words you would use to describe it to a buyer.',
    options: [],
  },
  {
    section: 'asset',
    key: 'location',
    label: 'Location',
    kind: 'text',
    unit: null,
    required: true,
    help: 'Town and county. The valuer will visit this address.',
    options: [],
  },
  {
    section: 'asset',
    key: 'estimatedValue',
    label: 'Your estimated value',
    kind: 'money',
    unit: 'KES',
    required: false,
    help: 'Your own estimate. The independent valuation is what the offer is priced off, and it may differ.',
    options: [],
  },
  {
    section: 'ownership',
    key: 'ownershipType',
    label: 'How you hold it',
    kind: 'select',
    unit: null,
    required: true,
    help: 'As it appears on the ownership document.',
    options: ['Sole ownership', 'Joint ownership', 'Company', 'Trust', 'Other'],
  },
  {
    section: 'ownership',
    key: 'encumbrances',
    label: 'Existing charges or loans',
    kind: 'long_text',
    unit: null,
    required: false,
    help: 'Any charge, caution, caveat or loan secured on this asset. An undisclosed charge is found by the register search, and found late.',
    options: [],
  },
  {
    section: 'tokenization',
    key: 'proposedTokens',
    label: 'Tokens you propose to issue',
    kind: 'number',
    unit: null,
    required: true,
    help: 'A whole number. The trust generates the final supply from the approved valuation.',
    options: [],
  },
  {
    section: 'tokenization',
    key: 'proposedUnitValue',
    label: 'Proposed value per token',
    kind: 'money',
    unit: 'KES',
    required: true,
    help: 'What you would like one token to be worth.',
    options: [],
  },
  {
    section: 'tokenization',
    key: 'tokenizedPct',
    label: 'Share of the asset to tokenize',
    kind: 'rate',
    unit: 'bps',
    required: true,
    help: 'In basis points. 10000 is the whole asset; 4000 is 40 per cent, with you keeping the rest.',
    options: [],
  },
  {
    section: 'tokenization',
    key: 'intendedUse',
    label: 'What the capital is for',
    kind: 'long_text',
    unit: null,
    required: true,
    help: 'What you intend to do with what you raise. Investors are shown this.',
    options: [],
  },
  {
    section: 'tokenization',
    key: 'incomeSharing',
    label: 'Income sharing',
    kind: 'select',
    unit: null,
    required: true,
    help: 'How often income is distributed to holders.',
    options: ['Monthly', 'Quarterly', 'Annually', 'On exit only'],
  },
  {
    section: 'tokenization',
    key: 'lockupMonths',
    label: 'Lock-up',
    kind: 'number',
    unit: 'months',
    required: false,
    help: 'Months before a holder may sell. Leave blank for none.',
    options: [],
  },
];

const BY_PRODUCT: Record<string, { fields: Field[]; documents: ProductConfig['documents'] }> = {
  property_fractional: {
    fields: [
      f(
        'titleNumber',
        'Title number',
        'text',
        null,
        true,
        'Exactly as it appears on the title deed. This is what the register is searched on.',
      ),
      f(
        'propertyType',
        'Property type',
        'select',
        null,
        true,
        'Residential, commercial, mixed use, industrial.',
        ['Residential', 'Commercial', 'Mixed use', 'Industrial'],
      ),
      f(
        'landSize',
        'Land size',
        'area',
        'acres',
        false,
        'As stated on the title, not as measured.',
      ),
      f(
        'builtUpArea',
        'Built-up area',
        'area',
        'sqm',
        false,
        'Gross floor area on the approved architectural drawing.',
      ),
      f(
        'units',
        'Number of units',
        'number',
        null,
        false,
        'Lettable units. One for a single dwelling.',
      ),
      f('constructionYear', 'Year built', 'number', null, false, 'Year of practical completion.'),
      f(
        'condition',
        'Condition',
        'select',
        null,
        false,
        'The valuer will form their own view; this is yours.',
        ['New', 'Good', 'Fair', 'Requires works'],
      ),
      f(
        'occupancy',
        'Current occupancy',
        'rate',
        'bps',
        false,
        'In basis points. 9200 is 92 per cent let.',
      ),
      f(
        'monthlyIncome',
        'Monthly rental income',
        'money',
        'KES',
        false,
        'Gross, before outgoings, as currently collected.',
      ),
      f(
        'amenities',
        'Amenities',
        'long_text',
        null,
        false,
        'Lift, borehole, parking, solar, security.',
      ),
    ],
    documents: [
      d(
        'owner_identification',
        true,
        1,
        'Your national ID or passport. For a company, the certificate of incorporation and the ID of a signatory.',
      ),
      d('title_deed', true, 1, 'The title deed for the property, in full.'),
      d(
        'property_photograph',
        true,
        4,
        'At least four: the frontage, the interior, the surroundings, and any outbuildings.',
      ),
      d(
        'search_certificate',
        false,
        1,
        'An official search, if you have obtained one recently. We will run our own.',
      ),
      d('rates_clearance', false, 1, 'County rates clearance certificate.'),
      d('tax_compliance', false, 1, 'KRA tax compliance certificate.'),
      d('insurance_policy', false, 1, 'Current cover on the building.'),
      d('lease_agreement', false, 1, 'Tenancy agreements, where the property is let.'),
      d(
        'valuation_report',
        false,
        1,
        'Any recent valuation you already hold. An independent valuation is still carried out.',
      ),
    ],
  },
  land_fractional: {
    fields: [
      f(
        'titleNumber',
        'Title number',
        'text',
        null,
        true,
        'Exactly as it appears on the title deed.',
      ),
      f('landSize', 'Land size', 'area', 'acres', true, 'As stated on the title.'),
      f(
        'tenure',
        'Tenure',
        'select',
        null,
        true,
        'Freehold, or leasehold with the unexpired term.',
        ['Freehold', 'Leasehold'],
      ),
      f(
        'leaseYearsRemaining',
        'Unexpired lease term',
        'years',
        'years',
        false,
        'Leasehold only. Years remaining on the lease.',
      ),
      f(
        'currentUse',
        'Current use',
        'text',
        null,
        false,
        'Agricultural, residential, commercial, undeveloped.',
      ),
    ],
    documents: [
      d('owner_identification', true, 1, 'Your national ID or passport.'),
      d('title_deed', true, 1, 'The title deed for the parcel.'),
      d('survey_plan', true, 1, 'The survey plan or mutation, showing the beacons.'),
      d('property_photograph', true, 2, 'At least two showing the parcel and its access.'),
      d('search_certificate', false, 1, 'An official search, if you hold a recent one.'),
      d('rates_clearance', false, 1, 'Land rates or rent clearance.'),
    ],
  },
  vehicle_fleet: {
    fields: [
      f(
        'registration',
        'Registration number',
        'text',
        null,
        true,
        'As on the logbook. One per vehicle; for a fleet, the lead vehicle.',
      ),
      f('makeModel', 'Make and model', 'text', null, true, 'As on the logbook.'),
      f('yearOfManufacture', 'Year of manufacture', 'number', null, true, 'As on the logbook.'),
      f('units', 'Number of vehicles', 'number', null, true, 'One for a single vehicle.'),
      f('odometer', 'Odometer', 'number', 'km', false, 'Current reading.'),
      f(
        'monthlyIncome',
        'Monthly income',
        'money',
        'KES',
        false,
        'What the vehicle or fleet currently earns, gross.',
      ),
    ],
    documents: [
      d('owner_identification', true, 1, 'Your national ID or passport.'),
      d('vehicle_logbook', true, 1, 'The logbook, showing you as the registered owner.'),
      d('property_photograph', true, 3, 'At least three: front, rear, and the interior.'),
      d('insurance_policy', true, 1, 'Current comprehensive cover.'),
      d('inspection_report', false, 1, 'A recent mechanical inspection, if you hold one.'),
    ],
  },
  receivable_discount: {
    fields: [
      f('debtor', 'Who owes it', 'text', null, true, "The obligor's registered name."),
      f(
        'faceValue',
        'Face value',
        'money',
        'KES',
        true,
        'The full amount owed, before any discount.',
      ),
      f('dueDate', 'Due date', 'date', null, true, 'When it falls due under the contract.'),
      f(
        'contractRef',
        'Contract or invoice reference',
        'text',
        null,
        true,
        'What identifies this receivable to the obligor.',
      ),
      f(
        'recourse',
        'Recourse',
        'long_text',
        null,
        false,
        'What happens if the obligor does not pay.',
      ),
    ],
    documents: [
      d('owner_identification', true, 1, 'Your national ID or passport.'),
      d('invoice', true, 1, 'The invoice or the contract the receivable arises under.'),
      d(
        'other_supporting',
        false,
        1,
        'Delivery notes, acceptance certificates, or anything evidencing performance.',
      ),
    ],
  },
  inventory_commodity: {
    fields: [
      f(
        'commodity',
        'What it is',
        'text',
        null,
        true,
        'Coffee, tea, maize, copper, finished goods.',
      ),
      f('quantity', 'Quantity', 'number', null, true, 'In the unit below.'),
      f('quantityUnit', 'Unit', 'text', null, true, 'Kilogrammes, tonnes, bags, units.'),
      f(
        'gradeSpec',
        'Grade or specification',
        'text',
        null,
        false,
        'The grade the quantity is certified at.',
      ),
      f(
        'storageSite',
        'Where it is stored',
        'text',
        null,
        true,
        'The warehouse or store. The valuer will inspect it there.',
      ),
    ],
    documents: [
      d('owner_identification', true, 1, 'Your national ID or passport.'),
      d('other_supporting', true, 1, 'The warehouse receipt or storage certificate.'),
      d('property_photograph', true, 2, 'At least two showing the goods in store.'),
      d('inspection_report', false, 1, 'A grading or quality certificate, where one exists.'),
      d('insurance_policy', false, 1, 'Cover on the stored goods.'),
    ],
  },
};

function f(
  key: string,
  label: string,
  kind: Field['kind'],
  unit: string | null,
  required: boolean,
  help: string,
  options: string[] = [],
): Field {
  return { section: 'asset', key, label, kind, unit, required, help, options };
}

function d(
  doc_type: string,
  required: boolean,
  min_count: number,
  note: string,
): ProductConfig['documents'][number] {
  return { doc_type, required, min_count, note };
}

// ---------------------------------------------------------------------------
// THE STATE
// ---------------------------------------------------------------------------

interface Application {
  application_id: string;
  reference: string;
  asset_id: string;
  product_code: string;
  status: TokenizationStatus;
  ownership_reference: string;
  details: Record<string, string>;
  proposal: Record<string, unknown>;
  documents: UploadedDocument[];
  history: LifecycleEvent[];
  offer: TokenizationOffer | null;
  valuation: TokenizationApplication['valuation'];
  token: TokenizationApplication['token'];
  information_requested: string | null;
  rejection_reason: string | null;
  created_at: string;
  submitted_at: string | null;
  updated_at: string;
}

interface Listing {
  listing_id: string;
  token_id: string;
  seller_id: string;
  /** Minor units. */
  total: bigint;
  remaining: bigint;
  minimum: bigint;
  price: string;
  quote_asset: string;
  note: string | null;
  listed_at: string;
  expires_at: string | null;
  cancelled: boolean;
}

const APPLICATIONS: Application[] = [];
const LISTINGS: Listing[] = [];

/**
 * The permitted moves, copied from `tokenization.lifecycle_transition`.
 *
 * The fixture refuses the same ones the database does. Without this, mock mode could reach any status
 * from any other and the tracker would be a decoration: the whole reason a state machine is worth
 * having is that it says no.
 */
const TRANSITIONS: Record<string, TokenizationStatus[]> = {
  draft: ['submitted', 'withdrawn'],
  submitted: ['under_vasp_review'],
  under_vasp_review: ['information_requested', 'under_trust_review', 'rejected'],
  information_requested: ['under_vasp_review', 'withdrawn'],
  under_trust_review: ['information_requested', 'approved_for_valuation', 'rejected'],
  approved_for_valuation: ['valuation_in_progress'],
  valuation_in_progress: ['valuation_submitted'],
  valuation_submitted: ['under_trust_reconciliation'],
  under_trust_reconciliation: ['approved_for_valuation', 'tokenization_offer_pending', 'rejected'],
  tokenization_offer_pending: ['offer_pending_user_approval'],
  offer_pending_user_approval: ['offer_pending_vasp_approval', 'tokenization_offer_pending'],
  offer_pending_vasp_approval: ['tokenization_authorized', 'tokenization_offer_pending'],
  tokenization_authorized: ['tokens_issued'],
  tokens_issued: ['listed_on_secondary_market', 'completed'],
  listed_on_secondary_market: ['completed', 'tokens_issued'],
};

export class FixtureRefusal extends Error {}

function move(
  app: Application,
  to: TokenizationStatus,
  actor: LifecycleEvent['actor'],
  action: string,
  note: string | null = null,
  actorRole: string | null = null,
) {
  if (!(TRANSITIONS[app.status] ?? []).includes(to)) {
    throw new FixtureRefusal(
      `A tokenization application cannot move from ${app.status} to ${to}. That is not a step in the lifecycle.`,
    );
  }
  app.history.push({
    from: app.status,
    to,
    actor,
    actor_id: actor === 'applicant' ? DEMO_OWNER : actor === 'system' ? null : id('op'),
    actor_role: actorRole,
    action,
    note,
    at: iso(),
  });
  app.status = to;
  app.updated_at = iso();
  app.information_requested = to === 'information_requested' ? note : null;
  if (to === 'rejected') app.rejection_reason = note;
}

function checklist(app: Application): ChecklistItem[] {
  const config = BY_PRODUCT[app.product_code];
  return config.documents.map((r) => {
    const provided = app.documents.filter(
      (doc) => doc.doc_type === r.doc_type && doc.status !== 'rejected',
    ).length;
    return {
      doc_type: r.doc_type,
      required: r.required,
      min_count: r.min_count,
      note: r.note,
      provided,
      rejected: app.documents.filter(
        (doc) => doc.doc_type === r.doc_type && doc.status === 'rejected',
      ).length,
      satisfied: !r.required || provided >= r.min_count,
    };
  });
}

function fieldsFor(code: string): Field[] {
  const own = (BY_PRODUCT[code]?.fields ?? []).map((x, i) => ({ ...x, key: x.key, sort: i }));
  const asset = COMMON_FIELDS.filter((x) => x.section === 'asset');
  const rest = COMMON_FIELDS.filter((x) => x.section !== 'asset');
  return [...asset, ...own, ...rest];
}

function view(app: Application): TokenizationApplication {
  const product = PRODUCTS.find((p) => p.code === app.product_code)!;
  return {
    application_id: app.application_id,
    reference: app.reference,
    status: app.status,
    stages: stagesFor(app.status),
    product: {
      code: product.code,
      name: product.name,
      structure: product.structure,
      classification: product.classification,
      asset_class: product.asset_class,
    },
    asset: {
      asset_id: app.asset_id,
      owner_id: DEMO_OWNER,
      ownership_reference: app.ownership_reference,
      verification_status:
        app.status === 'tokens_issued' || app.status === 'listed_on_secondary_market'
          ? 'tokenized'
          : 'submitted',
      legal_entity:
        product.asset_class === 'real_estate' || product.asset_class === 'land'
          ? {
              name: `${app.ownership_reference} Holdings Ltd`,
              registration_number: 'CPR/2026/' + app.asset_id.slice(-6),
            }
          : null,
    },
    details: app.details,
    proposal: app.proposal,
    information_requested: app.information_requested,
    rejection_reason: app.rejection_reason,
    assigned_valuer: app.status === 'draft' ? null : 'valuer-1',
    assigned_at: null,
    created_at: app.created_at,
    submitted_at: app.submitted_at,
    updated_at: app.updated_at,
    documents: app.documents,
    checklist: checklist(app),
    valuation: app.valuation,
    offer: app.offer,
    token: app.token,
    history: app.history,
  };
}

function summary(app: Application): ApplicationSummary {
  const product = PRODUCTS.find((p) => p.code === app.product_code)!;
  return {
    application_id: app.application_id,
    reference: app.reference,
    status: app.status,
    stages: stagesFor(app.status),
    product: product.name,
    asset_class: product.asset_class,
    name: app.details['description'] || app.ownership_reference,
    ownership_reference: app.ownership_reference,
    token_id: app.token?.token_id ?? null,
    token_symbol: app.token?.symbol ?? null,
    updated_at: app.updated_at,
  };
}

// ---------------------------------------------------------------------------
// THE OTHER PARTIES.
//
// The platform, the trust and the valuer are on origins this app cannot reach, so the fixture plays
// them - on a timer, after the owner has done their part, so the walk is demonstrable in one browser.
// Every step it takes is recorded against the party that would really have taken it, so the history
// on screen never reads as the owner having reviewed their own submission.
// ---------------------------------------------------------------------------

const STEP_MS = 2_600;

function scheduleTheOtherParties(app: Application) {
  const steps: [TokenizationStatus, LifecycleEvent['actor'], string, string | null][] = [
    ['under_vasp_review', 'vasp', 'Picked up for review', 'compliance_officer'],
    ['under_trust_review', 'vasp', 'Passed to the trust', 'compliance_officer'],
    ['approved_for_valuation', 'trust', 'Approved for valuation', 'trustee'],
    ['valuation_in_progress', 'valuer', 'Valuation started', 'valuation_officer'],
    ['valuation_submitted', 'valuer', 'Valuation submitted', 'valuation_officer'],
    ['under_trust_reconciliation', 'trust', 'Valuation under reconciliation', 'trustee'],
    ['tokenization_offer_pending', 'trust', 'Valuation approved', 'trustee'],
    ['offer_pending_user_approval', 'vasp', 'Tokenization offer issued', 'compliance_officer'],
  ];

  steps.forEach(([to, actor, action, role], i) => {
    setTimeout(
      () => {
        if (app.status === 'rejected' || app.status === 'withdrawn') return;
        try {
          move(app, to, actor, action, null, role);
        } catch {
          return; // the owner moved it elsewhere in the meantime. The state machine wins.
        }

        if (to === 'valuation_submitted') app.valuation = mockValuation(app);
        if (to === 'tokenization_offer_pending' && app.valuation) {
          app.valuation = {
            ...app.valuation,
            status: 'accepted',
            confirmed: true,
            reconciled_at: iso(),
          };
        }
        if (to === 'offer_pending_user_approval') app.offer = mockOffer(app);
      },
      STEP_MS * (i + 1),
    );
  });
}

function mockValuation(app: Application): TokenizationApplication['valuation'] {
  // Priced off the owner's own estimate where they gave one, so the offer they are shown relates to
  // the asset they described rather than to a constant.
  const estimate = BigInt(digits(app.details['estimatedValue']) || '10000000000');
  const adopted = (estimate * 95n) / 100n;

  return {
    report_id: id('rep'),
    method: 'market',
    basis: 'Market value',
    parameters: {
      inspectionDate: iso(-2 * DAY).slice(0, 10),
      inspectionType: 'Internal and external',
      comparableCount: '4',
      pricePerSqm: '2600000',
      marketability: 'Readily marketable',
    },
    indicated_value: String(adopted),
    adopted_value: String(adopted),
    asset_code: 'KES',
    variance_bps: 0,
    justification: null,
    forced_sale_value: String((adopted * 80n) / 100n),
    replacement_value: null,
    income_value: null,
    notes: 'Inspected internally and externally. Four comparables in the same suburb.',
    risk_observations: [
      { kind: 'encumbrance', severity: 'none', detail: 'Clean search, no charges registered.' },
    ],
    valuer: 'Nairobi Valuers Ltd',
    valuer_licence: 'VAL/2026/112',
    status: 'submitted',
    confirmed: false,
    effective_from: iso(),
    submitted_at: iso(),
    reconciled_at: null,
    reconciliation_note: null,
    report_document_id: null,
  };
}

function mockOffer(app: Application): TokenizationOffer {
  const adopted = BigInt(app.valuation?.adopted_value ?? '9500000000');
  const bps = BigInt(digits(app.details['tokenizedPct']) || '4000');
  const quantity = BigInt(digits(app.details['proposedTokens']) || '10000');
  const share = (adopted * bps) / 10_000n;
  const unit = share / quantity;

  return {
    offer_id: id('off'),
    status: 'pending_user',
    token: {
      name: app.details['description'] || 'Tokenized asset',
      symbol: symbolFor(app.details['description'] ?? 'ASSET'),
      quantity: Number(quantity),
      unit_value: String(unit),
      asset_code: 'KES',
      total_value: String(unit * quantity),
      tokenized_bps: Number(bps),
      classification: PRODUCTS.find((p) => p.code === app.product_code)!.classification,
    },
    valuation: { effective_from: iso(), method: 'market', valuer: 'Nairobi Valuers Ltd' },
    terms: {
      income: app.details['incomeSharing'] ?? 'Monthly',
      lockup_months: Number(digits(app.details['lockupMonths']) || '12'),
    },
    approvals: { trust: iso(), user: null, platform: null },
    fully_approved: false,
    rejection_reason: null,
    rejected_by: null,
    token_id: null,
    created_at: iso(),
  };
}

function symbolFor(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
    .replace(/[^A-Z]/g, '')
    .slice(0, 4);
  return `${letters || 'TKN'}-RE`;
}

function digits(value: string | undefined): string {
  return (value ?? '').replace(/[^\d]/g, '');
}

function toMinor(whole: string): bigint {
  const clean = String(whole).trim().replace(/,/g, '');
  const [head, tail = ''] = clean.split('.');
  return BigInt(head || '0') * ONE_TOKEN + BigInt((tail + '00000000').slice(0, 8));
}

function toWhole(minor: bigint): string {
  const whole = minor / ONE_TOKEN;
  const fraction = minor % ONE_TOKEN;
  return `${whole}.${fraction.toString().padStart(8, '0')}`;
}

function holdingFor(app: Application): TokenHolding | null {
  if (!app.token || !app.offer) return null;

  const supply = toMinor(String(app.offer.token.quantity));
  const listed = LISTINGS.filter(
    (l) => l.token_id === app.token!.token_id && !l.cancelled && l.remaining > 0n,
  ).reduce((sum, l) => sum + l.remaining, 0n);

  const product = PRODUCTS.find((p) => p.code === app.product_code)!;
  const unitValue = BigInt(app.offer.token.unit_value);

  return {
    token_id: app.token.token_id,
    name: app.token.name,
    symbol: app.token.symbol,
    state: app.token.state,
    units: toWhole(supply),
    units_listed: toWhole(listed),
    units_available: toWhole(supply - listed),
    fraction_bps: 10_000,
    total_supply: toWhole(supply),
    unit_value: String(unitValue),
    quote_asset: 'KES',
    value: String((supply * unitValue) / ONE_TOKEN),
    issued_at: app.updated_at,
    underlying: {
      asset_id: app.asset_id,
      asset_class: product.asset_class,
      ownership_reference: app.ownership_reference,
      description: app.details['description'] ?? null,
      location: app.details['location'] ?? null,
      held_by: `${app.ownership_reference} Holdings Ltd`,
      product: product.name,
      application_id: app.application_id,
      reference: app.reference,
      application_status: app.status,
    },
  };
}

function listingView(l: Listing): SecondaryListing {
  const app = APPLICATIONS.find((a) => a.token?.token_id === l.token_id);
  const product = app ? PRODUCTS.find((p) => p.code === app.product_code) : undefined;

  return {
    listing_id: l.listing_id,
    token_id: l.token_id,
    token_name: app?.token?.name ?? null,
    symbol: app?.token?.symbol ?? null,
    units_listed: toWhole(l.total),
    units_available: toWhole(l.remaining),
    minimum: toWhole(l.minimum),
    price: l.price,
    quote_asset: l.quote_asset,
    note: l.note,
    listed_at: l.listed_at,
    expires_at: l.expires_at,
    underlying: {
      asset_id: app?.asset_id ?? 'unknown',
      asset_class: product?.asset_class ?? 'real_estate',
      description: app?.details['description'] ?? null,
      location: app?.details['location'] ?? null,
      ownership_reference: app?.ownership_reference ?? 'unknown',
      held_by: app ? `${app.ownership_reference} Holdings Ltd` : null,
      total_supply: app?.offer ? toWhole(toMinor(String(app.offer.token.quantity))) : '0',
      issue_price: app?.offer?.token.unit_value ?? null,
      valuation: app?.valuation?.adopted_value ?? null,
      valuation_asset: 'KES',
      valued_at: app?.valuation?.effective_from ?? null,
    },
  };
}

// ---------------------------------------------------------------------------
// THE HANDLERS
// ---------------------------------------------------------------------------

const UPLOAD_POLICY: UploadPolicy = {
  accept: ['application/pdf', 'image/heic', 'image/jpeg', 'image/png', 'image/webp'],
  max_bytes: 10 * 1024 * 1024,
  note: 'Photographs and PDFs. Office documents are not accepted: an upload endpoint that takes anything takes an executable.',
};

function find(applicationId: string): Application {
  const app = APPLICATIONS.find((a) => a.application_id === applicationId);
  if (!app) throw new FixtureRefusal('We have no record of that application on your account.');
  return app;
}

export const tokenizationFixtures = {
  products: () => PRODUCTS,

  productConfig: (code: string): ProductConfig => {
    const product = PRODUCTS.find((p) => p.code === code);
    if (!product) throw new FixtureRefusal(`No such tokenization product: ${code}.`);
    return { ...product, fields: fieldsFor(code), documents: BY_PRODUCT[code]?.documents ?? [] };
  },

  uploadPolicy: () => UPLOAD_POLICY,

  applications: () => APPLICATIONS.map(summary),

  application: (applicationId: string) => view(find(applicationId)),

  start: (body: {
    product_code: string;
    ownership_reference: string;
    details: Record<string, string>;
    proposal?: Record<string, unknown>;
  }) => {
    const known = new Set(fieldsFor(body.product_code).map((x) => x.key));
    const unknown = Object.keys(body.details).filter((k) => !known.has(k));
    if (unknown.length > 0) {
      throw new FixtureRefusal('This tokenization product does not ask for: ' + unknown.join(', '));
    }

    const app: Application = {
      application_id: id('app'),
      reference: `TKN-2026-${String(APPLICATIONS.length + 1).padStart(6, '0')}`,
      asset_id: id('asset'),
      product_code: body.product_code,
      status: 'draft',
      ownership_reference: body.ownership_reference,
      details: body.details,
      proposal: body.proposal ?? {},
      documents: [],
      history: [],
      offer: null,
      valuation: null,
      token: null,
      information_requested: null,
      rejection_reason: null,
      created_at: iso(),
      submitted_at: null,
      updated_at: iso(),
    };
    APPLICATIONS.unshift(app);

    return {
      application_id: app.application_id,
      reference: app.reference,
      asset_id: app.asset_id,
      status: 'draft',
    };
  },

  update: (
    applicationId: string,
    body: { details?: Record<string, string>; proposal?: object },
  ) => {
    const app = find(applicationId);
    if (app.status !== 'draft' && app.status !== 'information_requested') {
      throw new FixtureRefusal(
        'This application is with a reviewer, so its answers cannot be changed.',
      );
    }
    if (body.details) app.details = { ...app.details, ...body.details };
    if (body.proposal) app.proposal = { ...app.proposal, ...body.proposal };
    app.updated_at = iso();
    return { updated: true };
  },

  upload: (
    applicationId: string,
    docType: string,
    fileName: string,
    size: number,
    replaces?: string,
  ) => {
    const app = find(applicationId);

    if (replaces) {
      app.documents = app.documents.filter((doc) => doc.document_id !== replaces);
    }

    const doc: UploadedDocument = {
      document_id: id('doc'),
      doc_type: docType,
      file_name: fileName,
      content_type: fileName.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
      byte_size: size,
      // A stand-in for the SERVER's SHA-256. Shaped like one so the screen that shows it is exercised,
      // and it is not a hash of anything: there is no server here to compute it.
      content_hash: Array.from(
        { length: 64 },
        () => '0123456789abcdef'[Math.floor(Math.random() * 16)],
      ).join(''),
      status: 'submitted',
      uploaded_at: iso(),
      review_note: null,
    };
    app.documents.push(doc);
    app.updated_at = iso();

    return { document_id: doc.document_id, content_hash: doc.content_hash };
  },

  removeDocument: (applicationId: string, documentId: string) => {
    const app = find(applicationId);
    if (app.status !== 'draft' && app.status !== 'information_requested') {
      throw new FixtureRefusal(
        'This application is with a reviewer, so its documents cannot be withdrawn.',
      );
    }
    app.documents = app.documents.filter((doc) => doc.document_id !== documentId);
    app.updated_at = iso();
    return { withdrawn: true };
  },

  submit: (applicationId: string) => {
    const app = find(applicationId);

    const missing = checklist(app)
      .filter((c) => !c.satisfied)
      .map((c) => c.doc_type);
    if (missing.length > 0) {
      throw new FixtureRefusal(
        'Some required documents are still missing: ' + missing.map((t) => docLabel(t)).join(', '),
      );
    }

    const missingFields = fieldsFor(app.product_code)
      .filter((x) => x.required && !String(app.details[x.key] ?? '').trim())
      .map((x) => x.label);
    if (missingFields.length > 0) {
      throw new FixtureRefusal(
        'Some required information is still missing: ' + missingFields.join(', '),
      );
    }

    if (app.status === 'information_requested') {
      move(app, 'under_vasp_review', 'applicant', 'Information provided');
    } else {
      move(app, 'submitted', 'applicant', 'Asset submitted');
      app.submitted_at = iso();
      scheduleTheOtherParties(app);
    }
    return { status: app.status, label: 'Asset submitted' };
  },

  decideOffer: (applicationId: string, accept: boolean, reason: string | null) => {
    const app = find(applicationId);
    if (!app.offer || app.offer.status !== 'pending_user') {
      throw new FixtureRefusal('There is no offer awaiting your decision on this application.');
    }

    if (!accept) {
      if (!reason?.trim()) {
        throw new FixtureRefusal(
          'Declining an offer needs a reason, so the terms can be reworked rather than guessed at.',
        );
      }
      app.offer = {
        ...app.offer,
        status: 'rejected',
        rejected_by: 'applicant',
        rejection_reason: reason,
      };
      move(app, 'tokenization_offer_pending', 'applicant', 'Offer declined by the owner', reason);
      return { status: app.status };
    }

    app.offer = {
      ...app.offer,
      status: 'pending_vasp',
      approvals: { ...app.offer.approvals, user: iso() },
    };
    move(app, 'offer_pending_vasp_approval', 'applicant', 'Offer approved by the owner');

    // The platform's approval, then the mint. Two operators sign the mint on their own origin; the
    // fixture stands in for both, and the history says `system` for the issuance exactly as the real
    // lifecycle does, because a mint runs off an action two people already signed.
    setTimeout(() => {
      if (!app.offer) return;
      app.offer = {
        ...app.offer,
        status: 'authorized',
        approvals: { ...app.offer.approvals, platform: iso() },
        fully_approved: true,
      };
      move(
        app,
        'tokenization_authorized',
        'vasp',
        'Tokenization authorized',
        null,
        'operations_officer',
      );
    }, STEP_MS);

    setTimeout(() => {
      if (!app.offer) return;
      const tokenId = id('tok');
      app.token = {
        token_id: tokenId,
        name: app.offer.token.name,
        symbol: app.offer.token.symbol,
        state: 'issued',
        total_supply: toWhole(toMinor(String(app.offer.token.quantity))),
        unit_value: app.offer.token.unit_value,
        quote_asset: 'KES',
      };
      app.offer = { ...app.offer, status: 'issued', token_id: tokenId };
      move(app, 'tokens_issued', 'system', 'Tokens issued');
    }, STEP_MS * 2);

    return { status: app.status };
  },

  portfolio: (): TokenHolding[] =>
    APPLICATIONS.map(holdingFor).filter((h): h is TokenHolding => h !== null),

  list: (
    tokenId: string,
    body: {
      tokens: string;
      unit_price: string;
      quote_asset?: string;
      minimum?: string | null;
      note?: string | null;
    },
  ) => {
    const app = APPLICATIONS.find((a) => a.token?.token_id === tokenId);
    if (!app || !app.offer) throw new FixtureRefusal('We have no record of that token.');

    if (app.token?.state !== 'issued') {
      throw new FixtureRefusal('This token is pledged as collateral, so it cannot be listed.');
    }

    const supply = toMinor(String(app.offer.token.quantity));
    const listed = LISTINGS.filter((l) => l.token_id === tokenId && !l.cancelled).reduce(
      (sum, l) => sum + l.remaining,
      0n,
    );
    const amount = toMinor(body.tokens);

    if (amount <= 0n) throw new FixtureRefusal('List a quantity greater than zero.');

    // The same refusal the database gives, in the same words, so the screen's error state is designed
    // against the message it will actually receive.
    if (amount > supply - listed) {
      throw new FixtureRefusal(
        `You have ${toWhole(supply - listed)} of this token available to list ` +
          `(${toWhole(listed)} is already listed), so ${toWhole(amount)} cannot be listed.`,
      );
    }

    const minimum = body.minimum ? toMinor(body.minimum) : amount;
    if (minimum <= 0n || minimum > amount) {
      throw new FixtureRefusal(
        'The minimum a buyer may take has to be between zero and the quantity listed.',
      );
    }

    const listing: Listing = {
      listing_id: id('lst'),
      token_id: tokenId,
      seller_id: DEMO_OWNER,
      total: amount,
      remaining: amount,
      minimum,
      price: body.unit_price,
      quote_asset: body.quote_asset ?? 'KES',
      note: body.note ?? null,
      listed_at: iso(),
      expires_at: null,
      cancelled: false,
    };
    LISTINGS.unshift(listing);

    if (app.status === 'tokens_issued') {
      move(app, 'listed_on_secondary_market', 'applicant', 'Listed on the secondary market');
    }

    return {
      listing_id: listing.listing_id,
      token_id: tokenId,
      units: toWhole(amount),
      unit_price: body.unit_price,
      quote_asset: listing.quote_asset,
      remaining_available: toWhole(supply - listed - amount),
    };
  },

  myListings: (): SecondaryListing[] =>
    LISTINGS.filter((l) => !l.cancelled && l.seller_id === DEMO_OWNER).map(listingView),

  cancelListing: (listingId: string) => {
    const listing = LISTINGS.find((l) => l.listing_id === listingId && !l.cancelled);
    if (!listing) throw new FixtureRefusal('We have no live listing of yours with that reference.');
    listing.cancelled = true;
    return { withdrawn: true };
  },

  market: (): SecondaryListing[] => LISTINGS.filter((l) => !l.cancelled).map(listingView),
};
